import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader, Card, DataTable, Button, Badge, Toast } from '../ui';
import Drawer from './Drawer';
import { OptionsEditor } from './AttributesPanel';
import {
  listFamilies,
  listAttributes,
  listLocales,
  getFamilyTemplate,
  createFamily,
  updateFamily,
  replaceFamilyAttributes,
  upsertSourceMapping,
  deleteSourceMapping,
  upsertDestinationMapping,
  deleteDestinationMapping,
  replaceAttributeOptions,
} from '../../services/pimApi';
import './pim.css';

// Option attribute types that carry per-value mappings (mirrors backend OPTION_TYPES).
const OPTION_TYPES = new Set(['simpleselect', 'multiselect']);
// Mirrors MappingsAdmin so the two screens stay consistent.
const SCOPE_STRATEGIES = ['global', 'channel_locale', 'channel', 'locale'];
const FIELD_KINDS = ['core', 'custom_field', 'category', 'price'];

const emptySource = (targetCode) => ({
  source_system: 'product_details',
  source_field: '',
  target_attribute_code: targetCode || '',
  scope_strategy: 'global',
  transform: 'identity',
  value_lookup_id: '',
  protect_when_enriched: false,
  enabled: true,
});

const emptyDest = (sourceCode) => ({
  destination_system: 'bigcommerce',
  field_kind: 'core',
  destination_field: '',
  source_attribute_code: sourceCode || '',
  source_alias: '',
  market_scoped: false,
  transform: 'identity',
  value_lookup_id: '',
  forced_value: '',
  enabled: true,
});

/* ---- Mapping form fields (source or destination) ----
 * `lockedAttributeCode` pins the field's own attribute and hides the picker.
 * When null (template-level destination), the attribute is selectable and the
 * alias/forced-value inputs are shown. */
function MappingFields({ kind, draft, setDraft, attributes, lockedAttributeCode }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  if (kind === 'source') {
    return (
      <>
        <div className="pim-field">
          <label>Source system</label>
          <input type="text" value={draft.source_system} onChange={(e) => set('source_system', e.target.value)} placeholder="e.g. product_details" />
        </div>
        <div className="pim-field">
          <label>Source field</label>
          <input type="text" value={draft.source_field} onChange={(e) => set('source_field', e.target.value)} placeholder="e.g. product_channel_locales.name" />
        </div>
        <div className="pim-field">
          <label>Target attribute</label>
          <input type="text" value={draft.target_attribute_code} disabled readOnly />
          <div className="pim-field-hint">Fixed to this field's attribute.</div>
        </div>
        <div className="pim-field">
          <label>Scope strategy</label>
          <select value={draft.scope_strategy} onChange={(e) => set('scope_strategy', e.target.value)}>
            {SCOPE_STRATEGIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <label className="pim-check" style={{ marginBottom: 16 }}>
          <input type="checkbox" checked={draft.protect_when_enriched} onChange={(e) => set('protect_when_enriched', e.target.checked)} />
          Protect when enriched (never overwrite manual enrichment)
        </label>
        <div className="pim-field">
          <label>Transform</label>
          <input type="text" value={draft.transform} onChange={(e) => set('transform', e.target.value)} placeholder="identity" />
          <div className="pim-field-hint">Name of a registered transform; defaults to identity.</div>
        </div>
        <div className="pim-field">
          <label>Value lookup id (optional)</label>
          <input type="number" value={draft.value_lookup_id} onChange={(e) => set('value_lookup_id', e.target.value)} />
        </div>
        <label className="pim-check">
          <input type="checkbox" checked={draft.enabled} onChange={(e) => set('enabled', e.target.checked)} />
          Enabled
        </label>
      </>
    );
  }
  return (
    <>
      <div className="pim-field">
        <label>Destination system</label>
        <input type="text" value={draft.destination_system} onChange={(e) => set('destination_system', e.target.value)} />
      </div>
      <div className="pim-field">
        <label>Field kind</label>
        <select value={draft.field_kind} onChange={(e) => set('field_kind', e.target.value)}>
          {FIELD_KINDS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="pim-field">
        <label>Destination field</label>
        <input type="text" value={draft.destination_field} onChange={(e) => set('destination_field', e.target.value)} placeholder="e.g. name, ItemType" />
      </div>
      {lockedAttributeCode ? (
        <div className="pim-field">
          <label>Source attribute</label>
          <input type="text" value={draft.source_attribute_code} disabled readOnly />
          <div className="pim-field-hint">Fixed to this field's attribute.</div>
        </div>
      ) : (
        <>
          <div className="pim-field">
            <label>Source attribute</label>
            <select value={draft.source_attribute_code} onChange={(e) => set('source_attribute_code', e.target.value)}>
              <option value="">(use alias / forced value)</option>
              {attributes.map((a) => (
                <option key={a.code} value={a.code}>{a.label} ({a.code})</option>
              ))}
            </select>
          </div>
          <div className="pim-field">
            <label>Source alias (display_*)</label>
            <input type="text" value={draft.source_alias} onChange={(e) => set('source_alias', e.target.value)} placeholder="e.g. display_name" />
          </div>
          <div className="pim-field">
            <label>Forced value (JSON, optional)</label>
            <input type="text" value={draft.forced_value} onChange={(e) => set('forced_value', e.target.value)} placeholder='e.g. true or "fixed"' />
          </div>
          <div className="pim-field-hint" style={{ marginBottom: 12 }}>
            Provide at least one of source attribute, alias, or forced value.
          </div>
        </>
      )}
      <label className="pim-check" style={{ marginBottom: 16 }}>
        <input type="checkbox" checked={draft.market_scoped} onChange={(e) => set('market_scoped', e.target.checked)} />
        Market-scoped (resolve per region)
      </label>
      <div className="pim-field">
        <label>Transform</label>
        <input type="text" value={draft.transform} onChange={(e) => set('transform', e.target.value)} placeholder="identity" />
      </div>
      <div className="pim-field">
        <label>Value lookup id (optional)</label>
        <input type="number" value={draft.value_lookup_id} onChange={(e) => set('value_lookup_id', e.target.value)} />
      </div>
      <label className="pim-check">
        <input type="checkbox" checked={draft.enabled} onChange={(e) => set('enabled', e.target.checked)} />
        Enabled
      </label>
    </>
  );
}

const srcSummary = (m) => `${m.source_system}.${m.source_field}${m.transform && m.transform !== 'identity' ? ` · ${m.transform}` : ''}`;
const dstSummary = (m) => `${m.destination_field} (${m.field_kind})${m.transform && m.transform !== 'identity' ? ` · ${m.transform}` : ''}`;
const dstSource = (m) => m.source_attribute_code || m.source_alias || (m.forced_value != null ? `=${JSON.stringify(m.forced_value)}` : '—');

export default function TemplateAdmin() {
  const [families, setFamilies] = useState([]);
  const [allAttributes, setAllAttributes] = useState([]);
  const [locales, setLocales] = useState([]);
  const [selectedCode, setSelectedCode] = useState('');
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);

  const [openFieldCode, setOpenFieldCode] = useState(null); // field drawer
  const [metaDraft, setMetaDraft] = useState(null); // {isNew, ...} meta drawer
  const [optionDraft, setOptionDraft] = useState(null); // option rows while editing a field
  const [mappingEditor, setMappingEditor] = useState(null); // {kind, draft, lockedAttributeCode}

  const flash = (message, error = false) => {
    setToast({ message, error });
    setTimeout(() => setToast(null), 3000);
  };

  const loadStatic = useCallback(async () => {
    try {
      const [f, a, l] = await Promise.all([listFamilies(), listAttributes(), listLocales()]);
      setFamilies(f);
      setAllAttributes(a);
      setLocales(l);
      if (!selectedCode && f.length) setSelectedCode(f[0].code);
    } catch (e) {
      flash('Failed to load templates', true);
    }
  }, [selectedCode]);

  const loadTemplate = useCallback(async (code) => {
    if (!code) {
      setTemplate(null);
      return;
    }
    setLoading(true);
    try {
      setTemplate(await getFamilyTemplate(code));
    } catch (e) {
      flash('Failed to load template', true);
      setTemplate(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatic(); }, [loadStatic]);
  useEffect(() => { loadTemplate(selectedCode); }, [selectedCode, loadTemplate]);

  const fields = useMemo(() => template?.fields || [], [template]);
  const openField = useMemo(
    () => fields.find((f) => f.attribute_code === openFieldCode) || null,
    [fields, openFieldCode],
  );

  // Re-seed the option draft whenever the open field changes (or its data reloads).
  useEffect(() => {
    if (openField && OPTION_TYPES.has(openField.type)) {
      setOptionDraft((openField.options || []).map((o) => ({
        code: o.code,
        sort_order: o.sort_order,
        labels: o.labels || {},
        source_value: o.source_value || '',
        destination_value: o.destination_value || '',
      })));
    } else {
      setOptionDraft(null);
    }
  }, [openField]);

  // ---- Family attribute (full-replace) writes ----
  // PUT /families/{id}/attributes is a FULL replace, so always send every field.
  const buildAttrsPayload = (overrideFn) =>
    fields.map((f, i) => {
      const base = {
        attribute_code: f.attribute_code,
        is_required: f.is_required,
        is_required_per_channel: f.is_required_per_channel,
        sort_order: i,
      };
      return overrideFn ? overrideFn(base, f, i) : base;
    });

  const saveFieldFlags = async (attrCode, flags) => {
    if (!template) return;
    setBusy(true);
    try {
      await replaceFamilyAttributes(
        template.family.id,
        buildAttrsPayload((base) =>
          base.attribute_code === attrCode ? { ...base, ...flags } : base),
      );
      flash('Field settings saved.');
      await loadTemplate(selectedCode);
    } catch (e) {
      flash(e?.response?.data?.detail || 'Save failed', true);
    } finally {
      setBusy(false);
    }
  };

  const addField = async (attrCode) => {
    if (!template || !attrCode) return;
    setBusy(true);
    try {
      const next = buildAttrsPayload();
      next.push({ attribute_code: attrCode, is_required: false, is_required_per_channel: false, sort_order: next.length });
      await replaceFamilyAttributes(template.family.id, next);
      flash('Field added.');
      await loadTemplate(selectedCode);
    } catch (e) {
      flash(e?.response?.data?.detail || 'Add failed', true);
    } finally {
      setBusy(false);
    }
  };

  const removeField = async (attrCode) => {
    if (!template) return;
    setBusy(true);
    try {
      const next = buildAttrsPayload()
        .filter((a) => a.attribute_code !== attrCode)
        .map((a, i) => ({ ...a, sort_order: i }));
      await replaceFamilyAttributes(template.family.id, next);
      flash('Field removed from template.');
      setOpenFieldCode(null);
      await loadTemplate(selectedCode);
    } catch (e) {
      flash(e?.response?.data?.detail || 'Remove failed', true);
    } finally {
      setBusy(false);
    }
  };

  // ---- Mapping writes ----
  const openNewMapping = (kind, lockedAttributeCode) =>
    setMappingEditor({
      kind,
      lockedAttributeCode: lockedAttributeCode || null,
      draft: kind === 'source' ? emptySource(lockedAttributeCode) : emptyDest(lockedAttributeCode),
    });

  const openEditMapping = (kind, row, lockedAttributeCode) => {
    const draft = kind === 'source'
      ? {
          id: row.id,
          source_system: row.source_system,
          source_field: row.source_field,
          target_attribute_code: lockedAttributeCode || row.target_attribute_code || '',
          scope_strategy: row.scope_strategy,
          transform: row.transform || 'identity',
          value_lookup_id: row.value_lookup_id ?? '',
          protect_when_enriched: row.protect_when_enriched,
          enabled: row.enabled,
        }
      : {
          id: row.id,
          destination_system: row.destination_system,
          field_kind: row.field_kind,
          destination_field: row.destination_field,
          source_attribute_code: lockedAttributeCode || row.source_attribute_code || '',
          source_alias: row.source_alias || '',
          market_scoped: row.market_scoped,
          transform: row.transform || 'identity',
          value_lookup_id: row.value_lookup_id ?? '',
          forced_value: row.forced_value != null ? JSON.stringify(row.forced_value) : '',
          enabled: row.enabled,
        };
    setMappingEditor({ kind, draft, lockedAttributeCode: lockedAttributeCode || null });
  };

  const saveMapping = async () => {
    const { kind, draft } = mappingEditor;
    setBusy(true);
    try {
      if (kind === 'source') {
        await upsertSourceMapping({
          ...draft,
          value_lookup_id: draft.value_lookup_id === '' ? null : Number(draft.value_lookup_id),
        });
      } else {
        let forced = null;
        if (draft.forced_value !== '') {
          try { forced = JSON.parse(draft.forced_value); } catch { forced = draft.forced_value; }
        }
        await upsertDestinationMapping({
          ...draft,
          source_attribute_code: draft.source_attribute_code || null,
          source_alias: draft.source_alias || null,
          value_lookup_id: draft.value_lookup_id === '' ? null : Number(draft.value_lookup_id),
          forced_value: forced,
        });
      }
      setMappingEditor(null);
      flash('Mapping saved.');
      await loadTemplate(selectedCode);
    } catch (e) {
      flash(e?.response?.data?.detail || 'Save failed', true);
    } finally {
      setBusy(false);
    }
  };

  const deleteMapping = async (kind, id) => {
    setBusy(true);
    try {
      if (kind === 'source') await deleteSourceMapping(id);
      else await deleteDestinationMapping(id);
      flash('Mapping deleted.');
      await loadTemplate(selectedCode);
    } catch (e) {
      flash(e?.response?.data?.detail || 'Delete failed', true);
    } finally {
      setBusy(false);
    }
  };

  // ---- Option value mappings ----
  const saveOptions = async () => {
    if (!openField || !optionDraft) return;
    setBusy(true);
    try {
      await replaceAttributeOptions(
        openField.attribute_id,
        optionDraft.map((o, i) => {
          const labels = Object.fromEntries(
            Object.entries(o.labels || {}).filter(([, v]) => (v || '').trim()),
          );
          const opt = { code: o.code, sort_order: i, labels };
          if ((o.source_value || '').trim()) opt.source_value = o.source_value.trim();
          if ((o.destination_value || '').trim()) opt.destination_value = o.destination_value.trim();
          return opt;
        }),
      );
      flash('Option mappings saved.');
      await loadTemplate(selectedCode);
    } catch (e) {
      flash(e?.response?.data?.detail || 'Save failed', true);
    } finally {
      setBusy(false);
    }
  };

  // ---- Template meta (create / edit) ----
  const openNewTemplate = () =>
    setMetaDraft({ isNew: true, code: '', label: '', attribute_as_label_code: '', attribute_as_image_code: '' });

  const openEditMeta = () => {
    if (!template) return;
    setMetaDraft({
      isNew: false,
      id: template.family.id,
      version: template.family.version,
      code: template.family.code,
      label: template.family.label,
      attribute_as_label_code: template.family.attribute_as_label_code || '',
      attribute_as_image_code: template.family.attribute_as_image_code || '',
    });
  };

  const saveMeta = async () => {
    if (!metaDraft.code.trim() || !metaDraft.label.trim()) {
      flash('Code and label are required.', true);
      return;
    }
    setBusy(true);
    try {
      const body = {
        label: metaDraft.label.trim(),
        attribute_as_label_code: metaDraft.attribute_as_label_code || null,
        attribute_as_image_code: metaDraft.attribute_as_image_code || null,
      };
      if (metaDraft.isNew) {
        await createFamily({ code: metaDraft.code.trim(), ...body });
        await loadStatic();
        setSelectedCode(metaDraft.code.trim());
      } else {
        await updateFamily(metaDraft.id, metaDraft.version, body);
        await loadTemplate(selectedCode);
      }
      setMetaDraft(null);
      flash('Template saved.');
    } catch (e) {
      flash(e?.response?.data?.detail || 'Save failed', true);
    } finally {
      setBusy(false);
    }
  };

  // ---- Grid columns ----
  const columns = useMemo(() => [
    {
      key: 'field',
      header: 'Field',
      render: (r) => (
        <div>
          <div>{r.label}</div>
          <div className="pim-tpl-sub"><code>{r.attribute_code}</code> · {r.type}{r.group_label ? ` · ${r.group_label}` : ''}</div>
        </div>
      ),
    },
    {
      key: 'required',
      header: 'Required',
      render: (r) => (
        <span style={{ display: 'flex', gap: 6 }}>
          {r.is_required ? <Badge variant="warning">required</Badge> : <Badge variant="neutral">optional</Badge>}
          {r.is_required_per_channel && <Badge variant="info">per-region</Badge>}
        </span>
      ),
    },
    {
      key: 'source',
      header: 'Source → PIM',
      render: (r) => (r.source_mappings.length
        ? <div className="pim-tpl-stack">{r.source_mappings.map((m) => <code key={m.id}>{srcSummary(m)}</code>)}</div>
        : <span className="pim-tpl-muted">—</span>),
    },
    {
      key: 'display',
      header: 'Display',
      render: (r) => (OPTION_TYPES.has(r.type)
        ? <Badge variant="neutral">{r.options.length} option{r.options.length === 1 ? '' : 's'}</Badge>
        : <span className="pim-tpl-muted">—</span>),
    },
    {
      key: 'destination',
      header: 'PIM → Destination',
      render: (r) => (r.destination_mappings.length
        ? <div className="pim-tpl-stack">{r.destination_mappings.map((m) => <code key={m.id}>{dstSummary(m)}</code>)}</div>
        : <span className="pim-tpl-muted">—</span>),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => <Button size="sm" variant="secondary" onClick={() => setOpenFieldCode(r.attribute_code)}>Edit</Button>,
    },
  ], []);

  const unattachedCols = useMemo(() => [
    { key: 'destination_field', header: 'Destination field', render: (r) => <code>{r.destination_field}</code> },
    { key: 'field_kind', header: 'Kind' },
    { key: 'source', header: 'Source', render: (r) => dstSource(r) },
    { key: 'transform', header: 'Transform' },
    {
      key: 'flags',
      header: 'Flags',
      render: (r) => (
        <span style={{ display: 'flex', gap: 6 }}>
          {r.market_scoped && <Badge variant="info">per-region</Badge>}
          {r.enabled ? <Badge variant="success">on</Badge> : <Badge variant="neutral">off</Badge>}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <Button size="sm" variant="secondary" onClick={() => openEditMapping('destination', r, null)}>Edit</Button>
          <Button size="sm" variant="danger" onClick={() => deleteMapping('destination', r.id)}>Delete</Button>
        </span>
      ),
    },
  ], []); // eslint-disable-line react-hooks/exhaustive-deps

  const isOptionField = openField && OPTION_TYPES.has(openField.type);
  const fieldHasSource = openField && openField.source_mappings.length > 0;

  return (
    <>
      <PageHeader
        title="Product Template"
        sub="A family's complete field list with each field's source → display → destination mapping, editable from one screen."
        actions={
          <span style={{ display: 'flex', gap: 8 }}>
            {template && <Button variant="secondary" onClick={openEditMeta}>Edit template</Button>}
            <Button onClick={openNewTemplate}>+ New template</Button>
          </span>
        }
      />

      <div className="pim-tpl-toolbar">
        <div className="pim-field" style={{ maxWidth: 320, marginBottom: 0 }}>
          <label>Template (family)</label>
          <select value={selectedCode} onChange={(e) => setSelectedCode(e.target.value)}>
            {families.length === 0 && <option value="">No templates yet</option>}
            {families.map((f) => (
              <option key={f.code} value={f.code}>{f.label} ({f.code})</option>
            ))}
          </select>
        </div>
      </div>

      <div className="pim-tpl-banner">
        Source and destination mappings are <strong>attribute-global</strong> — editing one here changes it
        for every template that uses that attribute. Only the Required flags are specific to this template.
      </div>

      <Card
        title="Fields"
        desc="Each row is a field in this template."
        actions={
          <AddFieldPicker available={template?.available_attributes || []} disabled={busy || !template} onAdd={addField} />
        }
        flush
      >
        <DataTable columns={columns} rows={fields} rowKey={(r) => r.attribute_id} loading={loading} />
      </Card>

      <Card
        title="Template-level destination mappings"
        desc="Outbound mappings driven by an alias or a forced value (not tied to a field)."
        actions={<Button size="sm" onClick={() => openNewMapping('destination', null)} disabled={!template}>+ Add</Button>}
        flush
      >
        <DataTable
          columns={unattachedCols}
          rows={template?.unattached_destination_mappings || []}
          rowKey={(r) => r.id}
          loading={loading}
        />
      </Card>

      {/* Field editor drawer */}
      {openField && (
        <Drawer
          title={openField.label}
          sub={`${openField.attribute_code} · ${openField.type}`}
          onClose={() => setOpenFieldCode(null)}
          footer={<Button variant="secondary" onClick={() => setOpenFieldCode(null)}>Close</Button>}
        >
          {/* Field membership */}
          <div className="pim-tpl-section">
            <h3>In this template</h3>
            <label className="pim-check">
              <input
                type="checkbox"
                checked={openField.is_required}
                disabled={busy}
                onChange={(e) => saveFieldFlags(openField.attribute_code, { is_required: e.target.checked })}
              />
              Required
            </label>
            <label className="pim-check">
              <input
                type="checkbox"
                checked={openField.is_required_per_channel}
                disabled={busy}
                onChange={(e) => saveFieldFlags(openField.attribute_code, { is_required_per_channel: e.target.checked })}
              />
              Required per region
            </label>
            <div style={{ marginTop: 10 }}>
              <Button size="sm" variant="danger" disabled={busy} onClick={() => removeField(openField.attribute_code)}>
                Remove field from template
              </Button>
              <div className="pim-field-hint">The attribute and its global mappings are left intact.</div>
            </div>
          </div>

          {/* Source mappings */}
          <div className="pim-tpl-section">
            <div className="pim-tpl-section-head">
              <h3>Source → PIM</h3>
              <Button size="sm" onClick={() => openNewMapping('source', openField.attribute_code)} disabled={busy}>+ Add</Button>
            </div>
            {openField.source_mappings.length === 0
              ? <p className="pim-tpl-muted">No source mappings.</p>
              : openField.source_mappings.map((m) => (
                <div className="pim-list-row" key={m.id}>
                  <span className="grow"><code>{srcSummary(m)}</code> <span className="pim-tpl-sub">{m.scope_strategy}{m.enabled ? '' : ' · off'}</span></span>
                  <button type="button" className="pim-mini-btn" onClick={() => openEditMapping('source', m, openField.attribute_code)}>Edit</button>
                  <button type="button" className="pim-mini-btn" onClick={() => deleteMapping('source', m.id)}>Delete</button>
                </div>
              ))}
          </div>

          {/* Destination mappings */}
          <div className="pim-tpl-section">
            <div className="pim-tpl-section-head">
              <h3>PIM → Destination</h3>
              <Button size="sm" onClick={() => openNewMapping('destination', openField.attribute_code)} disabled={busy}>+ Add</Button>
            </div>
            {openField.destination_mappings.length === 0
              ? <p className="pim-tpl-muted">No destination mappings.</p>
              : openField.destination_mappings.map((m) => (
                <div className="pim-list-row" key={m.id}>
                  <span className="grow"><code>{dstSummary(m)}</code> <span className="pim-tpl-sub">{m.enabled ? '' : 'off'}</span></span>
                  <button type="button" className="pim-mini-btn" onClick={() => openEditMapping('destination', m, openField.attribute_code)}>Edit</button>
                  <button type="button" className="pim-mini-btn" onClick={() => deleteMapping('destination', m.id)}>Delete</button>
                </div>
              ))}
          </div>

          {/* Option value mappings */}
          {isOptionField && optionDraft && (
            <div className="pim-tpl-section">
              <div className="pim-tpl-section-head">
                <h3>Option value mappings</h3>
                <Button size="sm" onClick={saveOptions} disabled={busy}>Save options</Button>
              </div>
              <OptionsEditor
                options={optionDraft}
                locales={locales}
                attributeCode={openField.attribute_code}
                canSeed={fieldHasSource}
                flash={flash}
                onChange={setOptionDraft}
              />
            </div>
          )}
        </Drawer>
      )}

      {/* Mapping add/edit drawer (on top of field drawer) */}
      {mappingEditor && (
        <Drawer
          title={`${mappingEditor.draft.id ? 'Edit' : 'New'} ${mappingEditor.kind === 'source' ? 'source' : 'destination'} mapping`}
          onClose={() => setMappingEditor(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setMappingEditor(null)} disabled={busy}>Cancel</Button>
              <Button onClick={saveMapping} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
            </>
          }
        >
          <MappingFields
            kind={mappingEditor.kind}
            draft={mappingEditor.draft}
            setDraft={(updater) => setMappingEditor((e) => ({ ...e, draft: typeof updater === 'function' ? updater(e.draft) : updater }))}
            attributes={allAttributes}
            lockedAttributeCode={mappingEditor.lockedAttributeCode}
          />
        </Drawer>
      )}

      {/* Template meta drawer */}
      {metaDraft && (
        <Drawer
          title={metaDraft.isNew ? 'New template' : `Edit ${metaDraft.code}`}
          sub="A product definition: its code, label, and which attribute supplies the display label/image."
          onClose={() => setMetaDraft(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setMetaDraft(null)} disabled={busy}>Cancel</Button>
              <Button onClick={saveMeta} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
            </>
          }
        >
          <div className="pim-row">
            <div className="pim-field">
              <label>Code</label>
              <input type="text" value={metaDraft.code} disabled={!metaDraft.isNew} onChange={(e) => setMetaDraft((d) => ({ ...d, code: e.target.value }))} />
            </div>
            <div className="pim-field">
              <label>Label</label>
              <input type="text" value={metaDraft.label} onChange={(e) => setMetaDraft((d) => ({ ...d, label: e.target.value }))} />
            </div>
          </div>
          {!metaDraft.isNew && (
            <div className="pim-row">
              <div className="pim-field">
                <label>Label attribute</label>
                <select value={metaDraft.attribute_as_label_code} onChange={(e) => setMetaDraft((d) => ({ ...d, attribute_as_label_code: e.target.value }))}>
                  <option value="">(none)</option>
                  {fields.map((f) => <option key={f.attribute_code} value={f.attribute_code}>{f.label}</option>)}
                </select>
              </div>
              <div className="pim-field">
                <label>Image attribute</label>
                <select value={metaDraft.attribute_as_image_code} onChange={(e) => setMetaDraft((d) => ({ ...d, attribute_as_image_code: e.target.value }))}>
                  <option value="">(none)</option>
                  {fields.filter((f) => f.type === 'image' || f.type === 'image_carousel').map((f) => (
                    <option key={f.attribute_code} value={f.attribute_code}>{f.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {metaDraft.isNew && (
            <div className="pim-field-hint">Add fields and the label/image attribute after creating the template.</div>
          )}
        </Drawer>
      )}

      <Toast message={toast?.message} error={toast?.error} />
    </>
  );
}

/* Inline "add field" select + button used in the Fields card header. */
function AddFieldPicker({ available, disabled, onAdd }) {
  const [code, setCode] = useState('');
  return (
    <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <select value={code} onChange={(e) => setCode(e.target.value)} disabled={disabled || available.length === 0} className="pim-tpl-addsel">
        <option value="">{available.length === 0 ? 'No more attributes' : 'Add field…'}</option>
        {available.map((a) => (
          <option key={a.code} value={a.code}>{a.label} ({a.code})</option>
        ))}
      </select>
      <Button size="sm" disabled={disabled || !code} onClick={() => { onAdd(code); setCode(''); }}>Add</Button>
    </span>
  );
}
